import { useEffect, useState } from 'react';

/**
 * Mobile breakpoint, matching the `@media (max-width: 576px)` queries in the SCSS.
 * Single source of truth for both this hook and CSS — change in one place if the
 * breakpoint ever moves.
 */
export const MOBILE_BREAKPOINT = 576;

/**
 * Reactive replacement for the static `isMobile` constant in constants.tsx.
 *
 * The static const is evaluated ONCE at module load (`window.innerWidth < 576`)
 * and never updates. That creates a class of bugs where the JS layer thinks
 * desktop while the CSS `@media` queries — which DO re-evaluate on viewport
 * change — are in mobile mode. Resize the window, rotate a device, or switch
 * Chrome DevTools mobile emulation after page load: every gate hanging off
 * `isMobile` goes stale.
 *
 * This hook listens to `resize` and updates state, so consuming components
 * re-render with the current viewport answer.
 *
 * For migration: the static `isMobile` export stays in place for the ~47
 * existing call sites. Only navbar / layout components that DRIVE the mobile
 * UI need to switch to this hook — the rest can stay on the const for now
 * and migrate opportunistically.
 *
 * @example
 *   const isMobile = useIsMobile();
 *   return <div className={isMobile ? 'mobile-layout' : 'desktop-layout'} />;
 */
export function useIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState<boolean>(() => {
        // SSR / non-browser safety. Real envs won't hit this branch.
        if (typeof window === 'undefined') return false;
        return window.innerWidth < MOBILE_BREAKPOINT;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleResize = () => {
            const next = window.innerWidth < MOBILE_BREAKPOINT;
            // Only call setState when the boolean flips — avoids triggering
            // a re-render on every pixel of a resize drag.
            setIsMobile((prev) => (prev === next ? prev : next));
        };

        window.addEventListener('resize', handleResize);
        // Also fire once on mount in case the initial state was stale (rare,
        // but possible if the consumer mounted right around a viewport change).
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
}
